import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin, Send, Loader2, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Legal Basements",
  "Custom Homes",
  "Kitchen & Bath",
  "Painting",
  "Flooring",
  "Electric & Plumbing",
  "Emergency Services",
  "Other / Not sure",
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch within a few hours.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 lg:py-36 bg-[#FAF7F2] relative"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: Contact Info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#B08D5B]" />
              <span className="label-overline text-[10px] text-[#B08D5B]">Begin the Conversation</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#0B1B33] leading-[1.05]">
              A free, <span className="italic text-[#B08D5B]">no-pressure</span>{" "}
              quote within 24 hours.
            </h2>
            <p className="mt-7 font-body text-stone-600 leading-[1.85] text-[15px] max-w-md">
              Tell us about your project and we'll get back with a clear estimate, timeline, and
              next steps. For urgent issues, call our emergency line anytime.
            </p>

            <div className="mt-10 space-y-px bg-[#EDE6D8]" data-testid="contact-info-list">
              <a
                href="tel:+14168264590"
                className="flex items-center gap-5 p-5 bg-white hover:bg-[#FAF7F2] group transition-colors duration-500"
                data-testid="contact-phone-1"
              >
                <div className="w-11 h-11 border border-[#B08D5B]/40 text-[#B08D5B] flex items-center justify-center flex-shrink-0 group-hover:bg-[#B08D5B] group-hover:text-white transition-colors duration-500">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <div className="label-overline text-[9px] text-stone-500">Main Line</div>
                  <div className="font-heading font-medium text-[#0B1B33] text-lg mt-1">
                    +1 416 826 4590
                  </div>
                </div>
              </a>

              <a
                href="tel:+16474466869"
                className="flex items-center gap-5 p-5 bg-white hover:bg-[#FAF7F2] group transition-colors duration-500"
                data-testid="contact-phone-2"
              >
                <div className="w-11 h-11 border border-[#DC2626]/40 text-[#DC2626] flex items-center justify-center flex-shrink-0 group-hover:bg-[#DC2626] group-hover:text-white transition-colors duration-500">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <div className="label-overline text-[9px] text-[#DC2626]">24/7 Emergency</div>
                  <div className="font-heading font-medium text-[#0B1B33] text-lg mt-1">
                    +1 647 446 6869
                  </div>
                </div>
              </a>

              <a
                href="mailto:primegtaremodeling@gmail.com"
                className="flex items-center gap-5 p-5 bg-white hover:bg-[#FAF7F2] group transition-colors duration-500"
                data-testid="contact-email"
              >
                <div className="w-11 h-11 border border-[#B08D5B]/40 text-[#B08D5B] flex items-center justify-center flex-shrink-0 group-hover:bg-[#B08D5B] group-hover:text-white transition-colors duration-500">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="label-overline text-[9px] text-stone-500">Email</div>
                  <div className="font-heading font-medium text-[#0B1B33] text-base mt-1 break-all">
                    primegtaremodeling@gmail.com
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-5 p-5 bg-white">
                <div className="w-11 h-11 border border-[#B08D5B]/40 text-[#B08D5B] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="label-overline text-[9px] text-stone-500">Service Area</div>
                  <div className="font-heading font-medium text-[#0B1B33] mt-1">
                    Greater Toronto Area
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-body">
                    Toronto · Mississauga · Brampton · Markham · Vaughan · Oakville
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-5 p-5 bg-white">
                <div className="w-11 h-11 border border-[#B08D5B]/40 text-[#B08D5B] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <div className="label-overline text-[9px] text-stone-500">Studio Hours</div>
                  <div className="font-heading font-medium text-[#0B1B33] mt-1">
                    Mon – Sat · 8 AM – 8 PM
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-body">
                    Emergency line 24 / 7
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#EDE6D8] p-8 sm:p-12 relative">
              {/* gold corner accent */}
              <div className="absolute top-0 left-0 w-12 h-px bg-[#B08D5B]" />
              <div className="absolute top-0 left-0 w-px h-12 bg-[#B08D5B]" />
              <div className="absolute bottom-0 right-0 w-12 h-px bg-[#B08D5B]" />
              <div className="absolute bottom-0 right-0 w-px h-12 bg-[#B08D5B]" />

              {submitted ? (
                <div
                  className="flex flex-col items-center text-center py-16"
                  data-testid="contact-success-state"
                >
                  <div className="w-16 h-16 border border-[#B08D5B] bg-[#B08D5B]/10 text-[#B08D5B] flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading text-3xl font-light text-[#0B1B33]">
                    Request received.
                  </h3>
                  <p className="mt-4 text-stone-600 font-body max-w-md leading-[1.8]">
                    A team member will reach out shortly. For urgent matters, please call{" "}
                    <a
                      href="tel:+14168264590"
                      className="text-[#B08D5B] font-semibold underline underline-offset-4"
                    >
                      +1 416 826 4590
                    </a>
                    .
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="ghost"
                    className="mt-10 border border-[#0B1B33]/20 rounded-none text-[#0B1B33] hover:bg-[#0B1B33] hover:text-white px-7 h-11 text-[12px] uppercase tracking-[0.2em] font-medium transition-all duration-500"
                    data-testid="contact-send-another"
                  >
                    Send another request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="mb-8">
                    <div className="label-overline text-[10px] text-[#B08D5B] mb-2">
                      Project Inquiry Form
                    </div>
                    <h3 className="font-heading text-2xl text-[#0B1B33] font-medium">
                      Tell us about your project.
                    </h3>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label
                        htmlFor="name"
                        className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-600"
                      >
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Jane Doe"
                        className="mt-2 h-12 bg-transparent border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#B08D5B] font-body text-[15px]"
                        required
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="phone"
                        className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-600"
                      >
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+1 416 555 0123"
                        className="mt-2 h-12 bg-transparent border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#B08D5B] font-body text-[15px]"
                        required
                        data-testid="contact-input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="email"
                      className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-600"
                    >
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      className="mt-2 h-12 bg-transparent border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#B08D5B] font-body text-[15px]"
                      required
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="service"
                      className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-600"
                    >
                      Service Interest
                    </Label>
                    <Select value={form.service} onValueChange={(v) => update("service", v)}>
                      <SelectTrigger
                        id="service"
                        className="mt-2 h-12 bg-transparent border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0 [&>span]:font-body [&>span]:text-[15px]"
                        data-testid="contact-select-service"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`contact-select-option-${opt
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label
                      htmlFor="message"
                      className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-600"
                    >
                      Project Details *
                    </Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Tell us about your project, square footage, timeline..."
                      rows={4}
                      className="mt-2 bg-transparent border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#B08D5B] resize-none font-body text-[15px]"
                      required
                      data-testid="contact-input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-13 py-3.5 bg-[#0B1B33] hover:bg-[#142847] text-white font-medium rounded-none transition-all duration-500 hover:shadow-[0_15px_30px_-10px_rgba(11,27,51,0.4)] disabled:opacity-70 tracking-[0.2em] text-[12px] uppercase mt-4"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-3" />
                        Request Free Quote
                      </>
                    )}
                  </Button>

                  <p className="text-[11px] text-stone-500 text-center font-body pt-2">
                    By submitting, you agree to be contacted about your project. We never share
                    your info.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
